import { html, LitElement, PropertyValues } from "lit";
import { queryAssignedElements, customElement, state } from "lit/decorators.js";
import style from './tab-layout.scss';

@customElement('tab-layout')
export class TabLayout extends LitElement {
    static override styles = style;

    @queryAssignedElements()
    private tabElements!: Array<HTMLElement>;

    @state()
    private tabNames: Array<string> = [];

    @state()
    private selectedTabId = 0;

    updateLabel(tabId: number | string, newLabel: string | ((lastName: string) => string)) {
        let id: number;
        if (typeof tabId == 'string') {
            id = this.tabElements.findIndex(el => el.id == tabId);
        } else {
            id = tabId;
        }
        if (id < 0 || id >= this.tabNames.length) return;
        let label: string;
        if (typeof newLabel == 'string') {
            label = newLabel;
        } else {
            label = newLabel(this.tabNames[id]);
        }
        this.tabNames[id] = label;
        this.tabElements[id].dataset.label = label;
        this.requestUpdate();
    }

    protected override render() {
        return html`
            <div id="tablist" role="tablist" tabindex=0 @click=${this._onClick} @keydown=${this._onKeydown}>
                ${this.tabNames.map((tab, index) => html`
                    <span role="tab" data-index=${index}
                        ?data-selected=${this.selectedTabId == index} aria-selected="${this.selectedTabId == index}"
                        aria-controls="tablist">${tab}</span>`
                )}
            </div>
            <div id="tabs">
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>
        `;
    }

    private _onSlotChange() {
        this.tabElements.forEach(el => el.role = 'tabpanel');
        this.tabNames = this.tabElements.map(el => el.dataset.label || '');
        this.selectedTabId = 0;
    }

    private _onKeydown(e: KeyboardEvent) {
        /* None of the required keyboard interaction uses special keys.
         * We should not block any extra browser shortcuts. */
        if (e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) return;

        if (e.key == 'ArrowLeft' || e.key == 'ArrowUp') {
            this.selectedTabId = this.selectedTabId > 0 ? this.selectedTabId - 1 : this.tabNames.length - 1;
        } else if (e.key == 'ArrowRight' || e.key == 'ArrowDown') {
            this.selectedTabId = this.selectedTabId < this.tabNames.length - 1 ? this.selectedTabId + 1 : 0;
        } else if (e.key == 'Home') {
            this.selectedTabId = 0;
        } else if (e.key == 'End') {
            this.selectedTabId = this.tabNames.length - 1;
        } else if (e.key == ' ' || e.key == 'Enter') {
            /* Preventing default because these keys would activate tab
             * if it wasn't activated automatically on focus. */
        } else return;
        e.preventDefault();
    }

    private _onClick(e: PointerEvent) {
        const newTabId = Number((e.target as HTMLElement).dataset.index);
        if (0 <= newTabId && newTabId < this.tabNames.length)
            this.selectedTabId = newTabId;
    }

    override updated(changedProperties: PropertyValues) {
        if (changedProperties.has('selectedTabId') || changedProperties.has('tabNames')) {
            this.tabElements.forEach((el, index) => el.style.display = index != this.selectedTabId ? 'none' : '');
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'tab-layout': TabLayout,
    }
}
